import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { LegalSection, LegalDisclaimer, LegalLastUpdated } from '../LegalLayout';
import { APP_NAME } from '../../config/branding';
import { LEGAL_ENTITY, MIN_AGE_YEARS } from '../legalConstants';

export default function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle={`How ${APP_NAME} collects, uses, stores, and protects your information.`}
    >
      <LegalLastUpdated />

      <LegalSection title="Introduction">
        <p>
          {LEGAL_ENTITY} (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) operates {APP_NAME}. This Privacy Policy
          explains what information we collect, why we collect it, how we use and share it, and the choices available
          to you. By using {APP_NAME}, you acknowledge this policy. If you do not agree, please do not use the service.
        </p>
      </LegalSection>

      <LegalSection title="Information we collect">
        <p><strong>Account information:</strong> When you register, we collect your email address, name, date of birth
          (to verify you meet our minimum age of {MIN_AGE_YEARS}), gender (as provided during signup), and authentication
          credentials (stored in hashed form).</p>
        <p><strong>Profile information:</strong> Handles, display names, profile and cover images, bios, and your active
          profile mode (personal or social). Personal and social identities are stored separately.</p>
        <p><strong>Content you create:</strong> Posts, comments, stories, club memberships, votes, messages, and media
          you upload.</p>
        <p><strong>Usage and device data:</strong> Log data such as IP address, browser type, access times, pages viewed,
          and actions taken — used for security, rate limiting, abuse prevention, and service improvement.</p>
        <p><strong>Authentication data:</strong> Session tokens (HTTP-only cookies where configured), OAuth identifiers
          if you sign in with Google, and security verification tokens (e.g., Cloudflare Turnstile responses — not stored
          long-term beyond verification).</p>
        <p><strong>Communications:</strong> Messages you send through contact forms, support requests, and safety reports.</p>
      </LegalSection>

      <LegalSection title="How we use information">
        <ul>
          <li>Provide, operate, and improve {APP_NAME}</li>
          <li>Authenticate users and maintain session security</li>
          <li>Enforce our Terms, Code of Conduct, and Acceptable Use policies</li>
          <li>Moderate content and respond to abuse reports</li>
          <li>Send service-related notifications (e.g., security alerts, account actions)</li>
          <li>Comply with legal obligations and respond to lawful requests</li>
          <li>Protect the rights, safety, and property of users and {LEGAL_ENTITY}</li>
        </ul>
        <p>We do not sell your personal information to third-party advertisers.</p>
      </LegalSection>

      <LegalSection title="Legal bases (where applicable)">
        <p>
          Depending on your location, we process personal data based on: performance of our contract with you (providing
          the service), legitimate interests (security, fraud prevention, product improvement), compliance with legal
          obligations, and your consent where required (e.g., optional communications).
        </p>
      </LegalSection>

      <LegalSection title="Sharing and disclosure">
        <p>We may share information with:</p>
        <ul>
          <li><strong>Service providers</strong> who assist with hosting, storage (including cloud object storage),
            email delivery, authentication, and security — bound by contractual obligations</li>
          <li><strong>Other users</strong> — content you publish publicly or to your connections is visible according
            to your profile mode and platform settings</li>
          <li><strong>Law enforcement or regulators</strong> when required by applicable law or to protect safety</li>
          <li><strong>Business transfers</strong> — in connection with a merger, acquisition, or asset sale, subject
            to continued protection</li>
        </ul>
      </LegalSection>

      <LegalSection title="Data retention">
        <p>
          We retain information for as long as your account is active or as needed to provide the service, comply with
          legal obligations, resolve disputes, and enforce agreements. Deleted content may persist in backups for a
          limited period before permanent removal.
        </p>
      </LegalSection>

      <LegalSection title="Security">
        <p>
          We implement technical and organizational measures including encrypted transport (HTTPS), password hashing,
          rate limiting, security headers, access controls, and token revocation on sign-out. No method of transmission
          or storage is completely secure; we cannot guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection title="Your rights and choices">
        <p>Depending on your jurisdiction, you may have the right to:</p>
        <ul>
          <li>Access, correct, or delete personal information</li>
          <li>Object to or restrict certain processing</li>
          <li>Data portability</li>
          <li>Withdraw consent where processing is consent-based</li>
          <li>Lodge a complaint with a supervisory authority</li>
        </ul>
        <p>
          To exercise these rights, please get in touch with us via the {' '}
          {/* <a href={`mailto:${SUPPORT_EMAIL}`} className="legal-inline-link">{SUPPORT_EMAIL}</a>.  */}
          {/* or use the{' '} */}
          <Link to="/contact" className="legal-inline-link">Contact</Link> form.
          We may need to verify
          your identity before fulfilling requests. See also our{' '}
          <Link to="/legal/regional" className="legal-inline-link">Regional Compliance</Link> page.
        </p>
      </LegalSection>

      <LegalSection title="Children">
        <p>
          {APP_NAME} is not intended for anyone under {MIN_AGE_YEARS} years of age. We do not knowingly collect personal
          information from children below this threshold. If you believe a child has provided us data, contact us and we
          will take appropriate steps to delete it.
        </p>
      </LegalSection>

      <LegalSection title="International transfers">
        <p>
          Your information may be processed in countries other than your own. Where required, we implement appropriate
          safeguards for cross-border data transfers.
        </p>
      </LegalSection>

      <LegalSection title="Third-party services">
        <p>
          {APP_NAME} may integrate third-party services (e.g., Google Sign-In, Cloudflare Turnstile). Those services have
          their own privacy policies. We encourage you to review them. Open-source components used in our stack are listed
          on our <Link to="/legal/open-source" className="legal-inline-link">Open Source Licenses</Link> page.
        </p>
      </LegalSection>

      <LegalSection title="Changes to this policy">
        <p>
          We may update this Privacy Policy at any time without prior notice. The &quot;Last updated&quot; date at the top
          reflects the most recent revision. Material changes may additionally be communicated through the service where
          appropriate.
        </p>
      </LegalSection>

      <LegalSection title="Contact">
        <p>
          Privacy inquiries: {' '}
          {/* <a href={`mailto:${SUPPORT_EMAIL}`} className="legal-inline-link">{SUPPORT_EMAIL}</a> or{' '} */}
          <Link to="/contact" className="legal-inline-link">Contact Us</Link>.
        </p>
      </LegalSection>

      <LegalDisclaimer />
    </LegalLayout>
  );
}
